import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Award, Handshake, HeartHandshake, ArrowRight } from 'lucide-react';
import { partnersService, sponsorsService, mediaUrl } from '../lib/api-service';
import type { PartnerListItem, Sponsor } from '../lib/types';

export function PartnersSponsorsStrip() {
  const [friends, setFriends] = useState<PartnerListItem[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([partnersService.list(), sponsorsService.list()])
      .then(([p, s]) => {
        setFriends(p.results);
        setSponsors(s.results);
      })
      .finally(() => setLoaded(true));
  }, []);

  const formalPartners = sponsors.filter((s) => s.kind === 'partner');
  const sponsorsOnly = sponsors.filter((s) => s.kind === 'sponsor');

  if (!loaded || (friends.length === 0 && formalPartners.length === 0 && sponsorsOnly.length === 0)) {
    return null;
  }

  return (
    <div className="mt-12 space-y-8 border-t pt-8">
      {friends.length > 0 && (
        <Section
          title="Prieteni"
          icon={Handshake}
          ctaTo="/parteneri"
          ctaLabel="Vezi toți prietenii"
        >
          {friends.slice(0, 12).map((p) => (
            <Tile
              key={p.id}
              to={`/partener/${p.slug}`}
              logo={p.logo}
              name={p.name}
              fallbackIcon={Handshake}
            />
          ))}
        </Section>
      )}

      {formalPartners.length > 0 && (
        <Section
          title="Parteneri"
          icon={HeartHandshake}
          ctaTo="/parteneri-sponsori"
          ctaLabel="Vezi toți partenerii"
        >
          {formalPartners.slice(0, 12).map((s) => (
            <Tile
              key={s.id}
              href={s.website_url}
              logo={s.logo}
              name={s.name}
              fallbackIcon={HeartHandshake}
            />
          ))}
        </Section>
      )}

      {sponsorsOnly.length > 0 && (
        <Section
          title="Sponsori"
          icon={Award}
          ctaTo="/parteneri-sponsori"
          ctaLabel="Vezi toți sponsorii"
        >
          {sponsorsOnly.slice(0, 12).map((s) => (
            <Tile
              key={s.id}
              href={s.website_url}
              logo={s.logo}
              name={s.name}
              fallbackIcon={Award}
            />
          ))}
        </Section>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: typeof Award;
  ctaTo: string;
  ctaLabel: string;
  children: React.ReactNode;
}

function Section({ title, icon: Icon, ctaTo, ctaLabel, children }: SectionProps) {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          <Icon className="h-4 w-4 text-[var(--vivid-purple)]" />
          {title}
        </h2>
        <Link
          to={ctaTo}
          className="flex items-center gap-1 text-xs font-medium text-[var(--vivid-purple)] hover:underline"
        >
          {ctaLabel}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
        {children}
      </div>
    </section>
  );
}

interface TileProps {
  to?: string;
  href?: string;
  logo: string;
  name: string;
  fallbackIcon: typeof Award;
}

function Tile({ to, href, logo, name, fallbackIcon: Fallback }: TileProps) {
  const content = (
    <div className="group flex h-20 flex-col items-center justify-center gap-1 rounded-lg border bg-white p-2 transition-all hover:border-[var(--vivid-purple)]/40 hover:shadow-sm">
      {logo ? (
        <img src={mediaUrl(logo)} alt={name} className="h-10 w-10 object-contain" />
      ) : (
        <Fallback className="h-6 w-6 text-muted-foreground/40" />
      )}
      <span className="line-clamp-1 w-full text-center text-[10px] font-medium text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        {name}
      </span>
    </div>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" title={name}>
        {content}
      </a>
    );
  }
  if (to) {
    return (
      <Link to={to} title={name}>
        {content}
      </Link>
    );
  }
  return content;
}
